import { useCallback, useEffect, useRef, useState } from 'react'
import { cancelEnrichmentRun, fetchEnrichmentRun, startEnrichmentRun } from '@/api/readings'
import { isEnrichmentRunActive, type EnrichmentRun } from '@/types/reading'

/**
 * How often to re-read a run while it is in flight. Runs take minutes (measured at
 * ~1.2s/word for Icelandic, ~3.6s for German), so 3s keeps the admin informed well
 * inside the 10s awareness target without meaningful request volume.
 */
const POLL_INTERVAL_MS = 3000

export interface EnrichmentRunState {
  run: EnrichmentRun | null
  loading: boolean
  starting: boolean
  error: string | null
  /** Start a run. If one is already active the server returns that one instead. */
  start: () => Promise<void>
  /** Stop the active run. A queued one ends at once; a working one at its next word. */
  cancel: () => Promise<void>
}

/**
 * Owns the lifecycle of a reading's enrichment run: reads the current one on mount
 * so progress survives a reload, polls while it is active, and stops as soon as it
 * reaches a terminal state.
 *
 * `enabled` is false for non-admins and unsupported languages, in which case nothing
 * is fetched at all.
 */
export function useEnrichmentRun(readingId: number, enabled: boolean): EnrichmentRunState {
  const [run, setRun] = useState<EnrichmentRun | null>(null)
  const [loading, setLoading] = useState(enabled)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Kept in a ref so the polling effect does not re-subscribe on every tick.
  const active = isEnrichmentRunActive(run)
  const mounted = useRef(true)

  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  const load = useCallback(async () => {
    if (!enabled) return
    try {
      const response = await fetchEnrichmentRun(readingId)
      if (!mounted.current) return
      setRun(response.data.data)
      // A recovered poll must clear the previous one's message. A German run polls
      // for half an hour, so one dropped request is close to certain, and a message
      // that outlives its cause leaves the admin watching an error while the run
      // quietly finishes behind it.
      setError(null)
    } catch {
      if (mounted.current) setError('Could not read the run status.')
    } finally {
      if (mounted.current) setLoading(false)
    }
  }, [readingId, enabled])

  // Read once on mount, so a run started in an earlier visit still shows progress.
  useEffect(() => {
    if (!enabled) {
      setLoading(false)
      return
    }
    void load()
  }, [enabled, load])

  // Poll only while the run is genuinely in flight; a run outliving this page is
  // expected, a timer is not.
  useEffect(() => {
    if (!enabled || !active) return

    const timer = setInterval(() => void load(), POLL_INTERVAL_MS)
    return () => clearInterval(timer)
  }, [enabled, active, load])

  const start = useCallback(async () => {
    setStarting(true)
    setError(null)
    try {
      const response = await startEnrichmentRun(readingId)
      if (mounted.current) setRun(response.data.data)
    } catch {
      if (mounted.current) setError('Could not start the run. Please try again.')
    } finally {
      if (mounted.current) setStarting(false)
    }
  }, [readingId])

  /**
   * A failed stop is not reported as an error. The common cause is losing a race — the
   * run finished on its own between deciding and clicking — so re-read the truth from
   * the server rather than guessing at a message. If it really is still going, the
   * button is still there to press again.
   */
  const cancel = useCallback(async () => {
    try {
      const response = await cancelEnrichmentRun(readingId)
      if (mounted.current) setRun(response.data.data)
    } catch {
      await load()
    }
  }, [readingId, load])

  return { run, loading, starting, error, start, cancel }
}
